import {Formik, Form as FormikForm} from 'formik'
import React, {useState} from 'react'
import * as Yup from 'yup'
import {FormikPasswordInput} from '~/src/design-system-shims'
import {userApi} from '../../../services/userApi'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {
  BodyBox,
  ButtonBox,
  CurrentPasswordLabel,
  EnterCurrentPasswordText,
  PleaseEnterText,
  StyledButton,
  VerifyPasswordTextfieldContainer,
} from './styles/ChangePasswordDialogStyles'

interface IProps {
  handleCancel(): void
  setCustomMessage: (value: string) => void
  setOldPassword: (value: string) => void

  setVerifyPasswordStep: (value: number) => void
}

export default function VerifyPasswordBody({
  handleCancel,
  setCustomMessage,
  setOldPassword,

  setVerifyPasswordStep,
}: IProps): React.ReactElement {
  const [returnError, setReturnError] = useState(false)
  const {showError, showSuccess} = useGlobalSnackbar()

  const initialFormValues = {
    verifyPassword: '',
  }

  const handleSubmit = async (
    values: {verifyPassword: string},
    {setErrors}: {setErrors: (errors: Record<string, boolean>) => void},
  ) => {
    setReturnError(false)

    setCustomMessage('')

    try {
      // API returns 201 on success - just proceed if no error is thrown
      await userApi.verifyUserPassword(values.verifyPassword)
      setOldPassword(values.verifyPassword)
      setVerifyPasswordStep(2)
    } catch (error: unknown) {
      // Show error message properly since we're not using unwrap()
      const err = error as {response?: {data?: {message?: string}}}
      const errorMessage = err.response?.data?.message ?? 'Failed to verify password'

      setReturnError(true)
      showError(new Error(errorMessage))
    }
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialFormValues}
      validationSchema={Yup.object({
        verifyPassword: Yup.string().required('Required'),
      })}>
      {({errors, values}) => {
        return (
          <FormikForm>
            <BodyBox>
              <EnterCurrentPasswordText>Enter Current Password</EnterCurrentPasswordText>
              <PleaseEnterText style={{color: returnError ? '#F7685B' : ''}}>
                {returnError
                  ? 'Password did not match. Retry typing password'
                  : 'Please enter & verify your current password'}
              </PleaseEnterText>
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <CurrentPasswordLabel>Current Password</CurrentPasswordLabel>
                <VerifyPasswordTextfieldContainer>
                  <FormikPasswordInput
                    InputLabelProps={{
                      shrink: true,
                    }}
                    name="verifyPassword"
                    label=""
                    type="password"
                    data-testid="changePassword-verifyPassword-textfield"
                  />
                </VerifyPasswordTextfieldContainer>
              </div>
            </BodyBox>
            <ButtonBox>
              <StyledButton
                data-testid="changePassword-cancel-button"
                aria-label="changePassword-cancel-button"
                style={{marginRight: '25px'}}
                onClick={() => {
                  handleCancel()
                }}
                variant="outlined"
                color="secondary"
                type="button">
                Cancel
              </StyledButton>
              <StyledButton
                disabled={
                  (errors.verifyPassword && errors.verifyPassword.length > 0) ||
                  values.verifyPassword.length === 0
                }
                data-testid="changePassword-saveFiles-button"
                aria-label="changePassword-saveFiles-button"
                sx={{color: 'white'}}
                variant="contained"
                color="primary"
                type="submit">
                Verify
              </StyledButton>
            </ButtonBox>
          </FormikForm>
        )
      }}
    </Formik>
  )
}
